import { Injectable } from '@angular/core';
import { FileManagerAPI } from './file-manager-api';
import { PipelineStageConfig, StatusObject } from '../../interfaces/biozoom-api/pipeline';
import { Observable, of, OperatorFunction } from 'rxjs';
import { filter, first, switchMap, tap } from 'rxjs/operators';
import { State } from '../../interfaces/biozoom-api/app-configuration';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import { AoxMeasurementResult } from '../../interfaces/biozoom-api/aox-measurement-result';
import { ScanNutritionService } from '../scanNutrition/scan-nutrition.service';
import { Platform } from '@ionic/angular';
import { HrvMeasurementResult } from '../../interfaces/biozoom-api/hrv-measurement-result';
import { PreciseAoxMeasurementResult } from '../../interfaces/biozoom-api/precise-aox-measurement-result';
import { environment } from '../../../environments/environment';
import { getLogs } from '../../methods/send-logs';
import {
  copyObject, flattenObject,
  getKeyVal,
  getScannerSoftwareVersionBySerialNumber,
  getScannerSoftwareVersionByWifiFirmware,
} from '../../methods/helpers';
import {processMeasurementAox, processMeasurementPreciseAox} from "../../methods/measurement";
import {IndexedMeasurementDatabase} from './indexed-database.service';
import {EnvironmentCapabilities} from './environment-capabilities.service';


const csvHeader = [
  'uuid',
  'aox',
  'aox_q',
  'aox_std',
  'aox_measurement_std',
  'quality_hand',
  'quality',
  'series_results',
  'series_q',
  'std_nn_raw',
  'mean_hr',
  'bmi',
  'hrv_q',
  'hrv_bio_age_raw',
  'height',
  'weight',
  'age',
  'gender',
];


@Injectable({
  providedIn: 'root'
})
export class FileManagerService {

  private fileNameIndices = 'measurement_indices.txt';


  constructor(
    private scanNutritionService: ScanNutritionService,
    private fileManagerAPI: FileManagerAPI,
    private platform: Platform,
    private indexedMeasurementDB: IndexedMeasurementDatabase,
    private environmentCapabilities: EnvironmentCapabilities,
  ) {
  }

  saveMeasurement = (pipelineConfig: PipelineStageConfig) => {
    return switchMap(async (stati: StatusObject) => {

      if (!this.platform.is('android')) {
        pipelineConfig.executeStage(
          pipelineConfig?.stageConfig?.eventTargets?.onInvalidPlatform,
          'onInvalidPlatform',
          pipelineConfig.stage
        );
        return;
      }

      if (stati._measurements.length === stati._aoxData.length) {
        pipelineConfig.executeStage(
          pipelineConfig?.stageConfig?.eventTargets?.onInsufficientMeasurements,
          'onInsufficientMeasurements',
          pipelineConfig.stage
        );
        return;
      }
      let additionalData = {
        status_before: stati._statusBefore ?? {},
        status_after: stati._statusAfter ?? {},
        app_version: environment.appVersion,
        config: environment.appDesignName,
        localTimestamp: new Date(Date.now()).toString(),
        localLog: getLogs(),
      };
      if (pipelineConfig.state.additionalData) {
        additionalData = { ...additionalData, ...pipelineConfig.state.additionalData };
      }

      let idx = pipelineConfig.getStep().idx;
      if (pipelineConfig.reduceQueries) {
        idx -= 1;
      }

      const resKey = pipelineConfig?.stageConfig?.resKey;
      const resGroup = pipelineConfig?.stageConfig?.resGroup;
      const localGroup = pipelineConfig.state?.[resGroup] ?? {};
      const numMeasurementsTotal = (getKeyVal(resKey, localGroup) as number) ?? pipelineConfig.getStep().numMeasurements ?? 4;


      const measurement: { 'data': string, type: string }[] = pipelineConfig.getStep().measurements;
      const data = JSON.stringify([pipelineConfig.getState(State.DEMO_MODE),
      pipelineConfig.getState(State.SELECTED_LANGUAGE),
      pipelineConfig.scanner.serial,
      measurement[idx]['data'],
      { ...additionalData, localTimestamp: Date.now(), isOfflineMeasurement: true, aox_subtype: measurement[idx]['type'] },
        idx,
      numMeasurementsTotal === (idx + 1)
      ]);

      const numMeasurements = await this.fileManagerAPI.writeMeasurement(data);

      if (!pipelineConfig.reduceQueries) {
        await pipelineConfig.setStep({ idx: idx + 1 });
      }
      pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
      return of(true);
    });
  }


  uploadMeasurement = (pipelineConfig: PipelineStageConfig) => {
    return switchMap((stati: StatusObject) => {
      if (!this.platform.is('android')) {
        pipelineConfig.executeStage(
          pipelineConfig?.stageConfig?.eventTargets?.onInvalidPlatform,
          'onInvalidPlatform',
          pipelineConfig.stage
        );
        return;
      }
      return of(stati).pipe(
        switchMap(async (_) => {
          const measurementData = await this.fileManagerAPI.readMeasurement();

          if (!measurementData) {
            pipelineConfig.executeStage(
              pipelineConfig?.stageConfig?.eventTargets?.onInsufficientMeasurements,
              'onInsufficientMeasurements',
              pipelineConfig.stage
            );
            return;
          }
          return measurementData;
        }),
        switchMap((measurementData) => {
          if (!measurementData) {
            pipelineConfig.executeStage(
              pipelineConfig?.stageConfig?.eventTargets?.onInsufficientMeasurements,
              'onInsufficientMeasurements',
              pipelineConfig.stage
            );
            return of(true);
          }
          const [isDemo, lang, serial, measurement, additionalData, idx, isLast] = JSON.parse(measurementData);
          return this.scanNutritionService.calculateMeasurementResult(
            Boolean(isDemo), lang, serial, measurement, additionalData
          ).pipe(
            switchMap((event: any) => {
              if ((event as HttpEvent<any>).type === undefined) {
                return of([true, false]);
              } else {
                event = event as HttpEvent<AoxMeasurementResult>;
                switch (event.type) {
                  case HttpEventType.Response: {
                    const res: AoxMeasurementResult = event.body;
                    // store the aox data
                    const aox = [...pipelineConfig.getStep().aox] ?? [];
                    if (aox.length >= (idx + 1)) {
                      aox[idx] = res;
                    } else {
                      aox.push(res);
                    }
                    pipelineConfig.setStep({
                      aoxData: aox
                    });
                    pipelineConfig.setState(State.AOX_MEASUREMENT_RESULTS, aox);
                    pipelineConfig.setState('resultAox', res, 'temp');
                    if (isLast) {
                      pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onLast, 'onLast', pipelineConfig.stage);
                    } else {
                      pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
                    }
                    return of([true, false]);
                  }
                  case HttpEventType.Sent: {
                    break;
                  }
                  case HttpEventType.UploadProgress:
                  case HttpEventType.DownloadProgress: {
                    const tmp = event.loaded / event.total;
                    pipelineConfig.setStep(
                      { progress: event.type === HttpEventType.UploadProgress ? (tmp * 0.9) : (tmp * 0.1) + 0.9, idx: 0 }
                    );
                    break;
                  }
                  default: {
                    break;
                  }
                }
              }
              return of([false, false]);
            }),
            filter(el => !!el[0]),
            tap((el) => {
              // pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
            })
          );

        })
      );
    });
  }

  deleteMeasurement = (pipelineConfig: PipelineStageConfig) => {
    return switchMap(async (stati: StatusObject) => {
      if (!this.platform.is('android')) {
        pipelineConfig.executeStage(
          pipelineConfig?.stageConfig?.eventTargets?.onInvalidPlatform,
          'onInvalidPlatform',
          pipelineConfig.stage
        );
        return;
      }
      const numMeasurements = await this.fileManagerAPI.deleteMeasurement();
      pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
      return of(true);
    });
  }

  mergeObjects = (o1, o2) => {
    const keysOrig = Object.keys(o1);
    for (const key of Object.keys(o2)) {
      if (!keysOrig.includes(key)) {
        o1[key] = o2[key];
      } else if (typeof o1[key] === 'object' && typeof o2[key] === 'object') {
        this.mergeObjects(o1[key], o2[key]);
      }
    }
  }

  addToCsv = (
    res: HrvMeasurementResult | AoxMeasurementResult | PreciseAoxMeasurementResult,
    pipelineStageConfig: PipelineStageConfig,
    resAOX?: PreciseAoxMeasurementResult,
    additionalData?: Record<string, any>
  ) => {
    if (!environment.appDesignSettings.logMeasurementResults) {
      return;
    }
    const resultCopy = copyObject(res);
    delete resultCopy.status_before;
    delete resultCopy.status_after;
    delete resultCopy.localLog;
    resultCopy.timestamp = Date.now();
    resultCopy.timestampString = new Date(resultCopy.timestamp).toString();
    this.mergeObjects(resultCopy, additionalData);

    return (async () => {
      const key = `results_${new Date().toISOString().split('T')[0]}`;
      try {
        const resArrRaw = await this.getCSV(key);
        let resArr;
        if (!resArrRaw) {
          resArr = [];
        } else {
          resArr = JSON.parse(resArrRaw);
        }
        resArr.push(resultCopy);
        await this.writeCSV(JSON.stringify(resArr), key);
      } catch (e) {
      }
    })();
  }

  downloadCsv = (key: string) => {
    const resArrRaw = localStorage.getItem(key);
    if (key === 'results') {
      const resArr = JSON.parse(resArrRaw);
      const returnCSV = [];
      let maxNum = 0;
      let maxRef = 3;

      for (const el of resArr) {
        if (!Object.keys(el).includes('precise_aox')) {
          continue;
        }
        while (true) {
          const keyTest = maxRef === 0 ? 'reference' : `reference${maxRef + 1}`;
          if (Object.keys(el).includes(keyTest)) {
            maxRef += 1;
          } else {
            break;
          }
        }
        maxNum = Math.max(maxNum, (el.series_results ?? []).length);
      }

      returnCSV.push([
        'timestamp',
        'user_id',
        'aox',
        'aox_std',
        'precise_quality_hand',
        'precise_quality_result',
        ...(() => {const r = []; for (let i = 0; i < maxRef; i++) {r.push(`reference${i + 1}`); } return r; })(),
        'timestampUnix',
        ...(() => {const r = []; for (let i = 0; i < maxRef; i++) {
          r.push(`aox${i + 1}`);
          r.push(`quality${i + 1}`);
          r.push(`error${i + 1}`);
        }          return r; })(),
      ]);
      for (const el of resArr) {
        if (!Object.keys(el).includes('precise_aox')) {
          continue;
        }
        returnCSV.push([
          el.timestampString ?? '',
          el.user_id ?? '',
          el.precise_aox ?? '',
          el.precise_aox_std ?? '',
          el.precise_quality_hand ?? '',
          el.precise_quality_result ?? '',
          ...(() => {const r = []; for (let i = 0; i < maxRef; i++) {
            const k = i === 0 ? 'reference' : `reference${i + 1}`;
            r.push(el[k] ?? '');
          }          return r; })(),
          el.timestamp ?? '',
          // el.series_results,
          ...(() => {const r = []; for (let i = 0; i < maxRef; i++) {
            r.push(el.series_results[i] ?? '');
            r.push(el.series_q[i] ?? '');
            r.push(el?.series_error?.[i] ?? '');
          }          return r; })(),
        ]);
      }
      return returnCSV.map(row => row.join(',')).join('\n');
    }
    return resArrRaw;

    // todo: actually end/download the file
  }


  pushToResultArray = (pipelineConfig: PipelineStageConfig) => {
    return switchMap((_stati: StatusObject) => {
      const resKey = pipelineConfig?.stageConfig?.resKey;
      const resGroup = pipelineConfig?.stageConfig?.resGroup;
      const localGroup = pipelineConfig.state?.[resGroup] ?? {};
      const localVal = getKeyVal(resKey, localGroup);
      if (!localVal) {
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onFail, 'onFail', pipelineConfig.stage);
      }

      const resArrRaw = localStorage.getItem('resultsCustom');
      let resArr: any[];
      if (!resArrRaw) {
        resArr = [];
      } else {
        resArr = JSON.parse(resArrRaw);
      }
      resArr.push(localVal);
      localStorage.setItem('resultsCustom', JSON.stringify(resArr));

      let clearVal = {};
      if (typeof localVal === 'number') {
        clearVal = 0;
      } else if (typeof localVal === 'string') {
        clearVal = '';
      }
      pipelineConfig.setState(resKey, clearVal, resGroup);
      pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
      return of(true);
    });
  }

  deleteMeasurements = (pipelineConfig: PipelineStageConfig) => {
    return switchMap(async (_stati: StatusObject) => {
      if (this.environmentCapabilities.hasIndexedDB() && !this.environmentCapabilities.canUseCordova()){
        await this.indexedMeasurementDB.clear();
      }
      else {
        for (const key of this.getKeys()) {
          localStorage.removeItem(key);
        }
        const keys = await this.listCSV();
        for (const key of keys) {
          await this.deleteCSV(key);
        }
      }
      pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
      return of(true);
    });
  }

  getKeys = () => {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('results')) {
        const val = localStorage.getItem(key);
        if (val && Array.isArray(JSON.parse(val))) {
          keys.push(key);
        }
      }
    }
    return keys;
  }

  export = (pipelineConfig: PipelineStageConfig) => {
    return switchMap(async (_stati: StatusObject) => {

      let results = [];
      const keys = await this.listCSV();

      for (const key of this.getKeys()) {
        const resArrRaw = localStorage.getItem(key);
        const resArr = JSON.parse(resArrRaw);
        if (!resArr) {
          continue;
        }
        results = [...results, ...resArr];
      }
      for (const key of keys) {
        const resArrRaw = await this.getCSV(key);
        const resArr = JSON.parse(resArrRaw);
        if (!resArr) {
          continue;
        }
        results = [...results, ...resArr];
      }

      if (results.length === 0) {
        console.log('no results found.');
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onError, 'onError', pipelineConfig.stage);
        return of(true);
      }
      const returnCSV = [];
      const settings = pipelineConfig.stageConfig.exportParameters;
      let newRow = [];
      const arrKeys = [];
      const uuidDict = {};

      let max = 0;
      for (const r of results) {
        if (!r.precise_aox) {
          continue;
        }
        max = Math.max(max, r.series_uuid.length);
      }
      results = results.sort((a, b) => a.timestamp - b.timestamp);

      for (let j = 0; j < results.length; j++) {
        let r = results[j];
        if (!r.precise_aox) {
          uuidDict[r.uuid] = r;
          continue;
        }
        if (!!r.series_errors && !Array.isArray(r.series_errors)) {
          continue;
        }
        if ((j + 1) < results.length && results[j + 1].mean_hr && results[j + 1].use_aox === r.aox) {
          r = {...r, ...results[j + 1]};
        }
        r.series_uuid = r.series_uuid.map(uuid => uuidDict[uuid]);
        processMeasurementPreciseAox(r, r.series_uuid);


        newRow = [];
        for (const s of settings) {
          const v = getKeyVal(s.key, r) ?? '';
          if (s.key.endsWith('.length')) {
            if (s.key === 'profile.length') {
              newRow.push(settings.filter(el => el.key.startsWith('profile.')).length - 1);
            } else {
              const keySplit = s.key.split('.');
              const key = keySplit.slice(0, keySplit.length - 1).join('.');
              const val = getKeyVal(key, r);
              if (Array.isArray(val)) {
                newRow.push(val.length);
              } else if (typeof val === 'object') {
                newRow.push(Object.keys(val).length);
              }
            }
          } else if (s.key.startsWith('series_uuid')) {
            const series: any[] = r.series_uuid;
            const key = s.key.split('.').slice(1).join('.');
            for (let i = 0; i < max; i++) {
              const v2 = getKeyVal(key, series[i] ?? {}) ?? '';
              newRow.push(v2);
            }
            if (!arrKeys.includes(s.key)) {
              arrKeys.push(s.key);
            }
          } else if (s.key.startsWith('series_errors')) {
            const keySplit = s.key.split('.')[1] ?? '';
            if (!keySplit || keySplit === '') {
              continue;
            }
            const allData = getKeyVal('series_errors', r) ?? [];
            for (const d of allData) {
              let val = d?.[keySplit] ?? undefined;
              if (val && typeof val === 'object') {
                val = val.scaled ?? 0;
              }
              val = Math.round(val * 100) / 100;
              newRow.push(val);
            }
            if (!arrKeys.includes(s.key)) {
              arrKeys.push(s.key);
            }
          } else if (Array.isArray(v)) {
            for (let i = 0; i < max; i++) {
              newRow.push(v[i] ?? '');
            }
            if (!arrKeys.includes(s.key)) {
              arrKeys.push(s.key);
            }
          } else {
            newRow.push(v);
          }
        }
        returnCSV.push(newRow.join(pipelineConfig.stageConfig.exportDelimiter ?? ';'));
      }

      newRow = [];
      for (const s of settings) {
        if (!(arrKeys.includes(s.key))) {
          newRow.push(s.caption);
        } else {
          for (let i = 0; i < max; i++) {
            newRow.push(`${s.caption}_${i + 1}`);
          }
        }
      }
      returnCSV.unshift(newRow.join(pipelineConfig.stageConfig.exportDelimiter ?? ';'));
      const returnString = returnCSV.join('\n');

      console.log('returnString::', returnString);

      if (this.environmentCapabilities.hasIndexedDB() && !this.environmentCapabilities.canUseCordova()){
        await this.downloadCSVFromIndexedDB(returnString, 'results.csv');
      }
      else {
        // cordova handling
        try {
          // @ts-ignore
          const file = window.cordova.file;
          this.writeCSV(returnString, 'results.csv').then(() => {
            // @ts-ignore
            window.plugins.socialsharing.share('Your biozoom result export.', 'Biozoom result file', `${file.externalDataDirectory}results.csv`);
          });
        } catch (e) {
          return;
        }
      }

      pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
      return of(true);
    });
  }

  deleteCSV = async (fileName: string) => {
    return this.fileManagerAPI.deleteData(fileName);
  }

  listCSV = async () => {
    const d = await this.fileManagerAPI.listData();

    return d.filter((e) => e.startsWith('results_'));
  }

  getCSV = async (fileName: string) => {
    return this.fileManagerAPI.readData(fileName);
  }

  writeCSV = async (data: string, fileName: string) => {
    await this.fileManagerAPI.writeData(data, fileName);
  }
  
  /**
   * Initializes the IndexedDB for storing measurements.
   * Creates a new instance of IndexedMeasurementDatabase and initiates its initialization process.
   * @returns A promise that resolves once the database is initialized.
   */
  createIndexedDB = async () => {
    this.indexedMeasurementDB = new IndexedMeasurementDatabase();
    await this.indexedMeasurementDB.init();
  }
  
  /**
   * Downloads a CSV file containing the filtered measurement data from IndexedDB.
   * This method uses a Blob and a temporary anchor element to trigger a browser download.
   *
   * @param csvContent - The content of the CSV to be downloaded.
   * @param [fileName='results.csv'] - The name of the file to be saved.
   * @returns A promise that resolves when the download process is completed.
   */
  downloadCSVFromIndexedDB = async (csvContent: string, fileName: string = 'results.csv') => {
    if (this.environmentCapabilities.canUseBlob()) {
      try{
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.style.display = 'none';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
      }
      catch (e) {
        console.error('Error downloading CSV:', e);
      }
    }
    else {
      console.error('Blob not supported');
    }
  }

  
  /**
   * Pipeline stage function that retrieves a JSON object from the state based on the provided pipeline configuration and saves it for
   * export.
   * @param pipelineConfig The configuration for the current pipeline stage, including the resKey and resGroup for locating the JSON object
   * in the state.
   * @returns An RxJS OperatorFunction that returns an Observable.
   */
  saveResultsFilteredForExport(pipelineConfig: PipelineStageConfig): OperatorFunction<StatusObject, Observable<any>> {
    return switchMap(async (_stati: StatusObject): Promise<Observable<any>> => {
      const resKey = pipelineConfig.stageConfig.resKey ?? '';
      const resGroup = pipelineConfig.stageConfig.resGroup ?? '';

      if (resKey === '' || resGroup === '') {
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onFail, 'onFail', pipelineConfig.stage);
        return of(true);
      }
      const jsonToSave: Record<string, unknown> = pipelineConfig.state[resGroup][resKey];

      await this.handleSaveResult(jsonToSave);

      pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
      return of(true);

    });
  }

  /**
   * Saves a JSON result object for future export. Depending on environment capabilities, the result is saved to IndexedDB or a CSV file.
   * @param jsonToSave The result object to be saved.
   */
  handleSaveResult = async (jsonToSave: Record<string, unknown>) => {
    // Check if indexDB is available on the current system --> e.g., in BrowserApplication
    if (this.environmentCapabilities.hasIndexedDB() && !this.environmentCapabilities.canUseCordova()){
      if (!this.indexedMeasurementDB.database) {
        await this.createIndexedDB();
      }
      await this.indexedMeasurementDB.add({createdAt: Date.now(), data: jsonToSave});
    }
    else {
      console.log('DEBUG: handleSaveResult');
      const key = `results_${new Date().toISOString().split('T')[0]}`;
      try {
        const resArrRaw = await this.getCSV(key);
        let resArr;
        if (!resArrRaw) {
          resArr = [];
        } else {
          resArr = JSON.parse(resArrRaw);
        }
        resArr.push(jsonToSave);
        await this.writeCSV(JSON.stringify(resArr), key);
        console.log('DEBUG: handleSaveResult: saved');
      } catch (e) {
      }
    }
    console.log('Implement Saving on Android / copy previous workflow.');
  }

  /**
   * Converts an array of result objects into a CSV string. Nested objects are flattened into dot-notated columns.
   * @param results An array of objects to be converted to CSV format.
   * @returns A CSV formatted string representing the results.
   */
  resultsToCSV = (results: Record<string, unknown>[]) => {
    if (results.length === 0) {
      return '';
    }
    const flatResults = results.map(result => flattenObject(result));

    const headers = Array.from(
        new Set(
            flatResults
                .map(obj => Object.keys(obj))
                .reduce((acc, keys) => acc.concat(keys), [] as string[])
        )
    );

    const rows = flatResults.map(row =>
        headers
            .map(header => `"${String(row[header] ?? '').replace(/"/g, '""')}"`)
            .join(',')
    );

    return [headers.join(','), ...rows].join('\n');
  }

  /**
   * Pipeline stage function that exports all filtered results. Depending on environment capabilities, results are gathered from IndexedDB
   * or CSV files and then either downloaded or shared via social sharing.
   * @param pipelineConfig The configuration for the current pipeline stage.
   * @returns An RxJS OperatorFunction that returns an Observable.
   */
  exportResultsFiltered(pipelineConfig: PipelineStageConfig): OperatorFunction<StatusObject, Observable<any>> {
    return switchMap(async (_stati: StatusObject): Promise<Observable<any>> => {

      let results = [];
      if (this.environmentCapabilities.hasIndexedDB() && !this.environmentCapabilities.canUseCordova()){
        const storedData = await this.indexedMeasurementDB.getAll();
        results = storedData.map(item => item.data);
        if (results.length === 0) {
          pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onError, 'onError', pipelineConfig.stage);
          return of(true);
        }
        const resultsCSV = this.resultsToCSV(results);
        await this.downloadCSVFromIndexedDB(resultsCSV, 'results.csv');
      }
      else {
        const keys = await this.listCSV();

        for (const key of this.getKeys()) {
          const resArrRaw = localStorage.getItem(key);
          const resArr = JSON.parse(resArrRaw);
          if (!resArr) {
            continue;
          }
          results = [...results, ...resArr];
        }
        for (const key of keys) {
          const resArrRaw = await this.getCSV(key);
          const resArr = JSON.parse(resArrRaw);
          if (!resArr) {
            continue;
          }
          results = [...results, ...resArr];
        }

        if (results.length === 0) {
          pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onError, 'onError', pipelineConfig.stage);
          return of(true);
        }

        const resultsCSV = this.resultsToCSV(results);

        try {
          // @ts-ignore
          const file = window.cordova.file;
          this.writeCSV(resultsCSV, 'results.csv').then(() => {
            // @ts-ignore
            window.plugins.socialsharing.share('Your biozoom result export.', 'Biozoom result file', `${file.externalDataDirectory}results.csv`);
          });
        } catch (e) {
          return;
        }
      }
      pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
      return of(true);
    });
  }
}
